import * as XLSX from "xlsx";
import JSZip from "jszip";

// Export the rendered grid so specification rows, offers and commercial terms
// stay in sync with the UI. Patch OOXML styles (SheetJS CE does not write them).
export async function comparisonWorkbook(table, title) {
  const values = [[title || "Quotation comparison"]];
  const merges = [];
  const styles = new Map();
  const occupied = new Set();
  const widths = [];
  const heights = [{ hpt: 30 }];
  const palette = [];
  const styleId = (cell) => {
    let element = cell;
    let color = "FFFFFF";
    while (element) {
      const channels = getComputedStyle(element).backgroundColor.match(/[\d.]+/g);
      if (channels && (channels.length < 4 || Number(channels[3]) > 0)) {
        color = channels.slice(0, 3).map((v) => Math.round(Number(v)).toString(16).padStart(2, "0")).join("").toUpperCase();
        break;
      }
      element = element.parentElement;
    }
    // Price badges use a gradient on a child element, not the cell background.
    // Excel uses a solid fill; use the badge's light color for the best rank.
    const rankedPrice = Array.from(cell.querySelectorAll("[data-price-rank]"))
      .sort((a, b) => Number(a.dataset.priceRank) - Number(b.dataset.priceRank))[0];
    if (rankedPrice) {
      const lightColor = getComputedStyle(rankedPrice).getPropertyValue("--price-light").trim();
      const fallback = { 1: "FFFBE3", 2: "FFF3DB", 3: "FFF1F1", 4: "E7A0A0" };
      color = /^#[\da-f]{6}$/i.test(lightColor)
        ? lightColor.slice(1).toUpperCase()
        : fallback[rankedPrice.dataset.priceRank] || color;
    }
    const bold = cell.tagName === "TH" || Boolean(rankedPrice);
    const key = `${color}:${bold}`;
    let index = palette.findIndex((entry) => entry.key === key);
    if (index < 0) index = palette.push({ key, color, bold }) - 1;
    return index + 1;
  };
  Array.from(table.rows).forEach((row, index) => {
    const r = index + 1;
    values[r] = values[r] || [];
    let c = 0;
    let height = 30;
    Array.from(row.cells).forEach((cell) => {
      while (occupied.has(`${r}:${c}`)) c++;
      const text = (cell.innerText || cell.textContent || "").trim();
      values[r][c] = text;
      const cols = cell.colSpan || 1;
      const rows = cell.rowSpan || 1;
      const width = Math.max(12, Math.min(55, cell.getBoundingClientRect().width / cols / 7));
      const style = styleId(cell);
      for (let y = r; y < r + rows; y++) {
        for (let x = c; x < c + cols; x++) {
          occupied.add(`${y}:${x}`);
          styles.set(XLSX.utils.encode_cell({ r: y, c: x }), style);
          widths[x] = Math.max(widths[x] || 12, width);
        }
      }
      const lines = text.split(/\n/).reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / Math.max(1, width * cols - 2))), 0);
      height = Math.max(height, Math.ceil((lines * 15 + 12) / rows));
      if (cols > 1 || rows > 1) merges.push({ s: { r, c }, e: { r: r + rows - 1, c: c + cols - 1 } });
      c += cols;
    });
    heights[r] = { hpt: Math.min(409, height) };
  });
  const sheet = XLSX.utils.aoa_to_sheet(values);
  styles.forEach((_, address) => { if (!sheet[address]) sheet[address] = { t: "s", v: "" }; });
  if (widths.length > 1) merges.unshift({ s: { r: 0, c: 0 }, e: { r: 0, c: widths.length - 1 } });
  sheet["!merges"] = merges;
  sheet["!cols"] = widths.map((wch) => ({ wch }));
  sheet["!rows"] = heights;
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Comparison");
  const zip = await JSZip.loadAsync(XLSX.write(workbook, { bookType: "xlsx", type: "array" }));
  const fills = palette.map(({ color }) => `<fill><patternFill patternType="solid"><fgColor rgb="FF${color}"/><bgColor indexed="64"/></patternFill></fill>`).join("");
  const formats = palette.map(({ bold }, i) => `<xf numFmtId="0" fontId="${bold ? 1 : 0}" fillId="${i + 2}" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>`).join("");
  zip.file("xl/styles.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/><color rgb="FF1E293B"/></font><font><b/><sz val="11"/><name val="Calibri"/><color rgb="FF1E293B"/></font></fonts><fills count="${palette.length + 2}"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>${fills}</fills><borders count="2"><border/><border>${["left", "right", "top", "bottom"].map((side) => `<${side} style="thin"><color rgb="FFCBD5E1"/></${side}>`).join("")}<diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="${palette.length + 1}"><xf numFmtId="0" fontId="1" fillId="0" borderId="1" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>${formats}</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`);
  const xml = await zip.file("xl/worksheets/sheet1.xml").async("string");
  zip.file("xl/worksheets/sheet1.xml", xml.replace(/<c\b([^>]*?)>/g, (tag, attrs) => {
    const address = attrs.match(/\br="([^"]+)"/)?.[1];
    return `<c${attrs.replace(/\s+s="[^"]*"/g, "")} s="${styles.get(address) || 0}">`;
  }));
  return zip.generateAsync({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}
